import { Box } from "@mui/system";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import CommCard from "src/common/CommCard";
import { CommonPagination } from "src/common/CommonPagination";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import type { IOfflineExhibitorListItem } from "../type/offlineExhibitorType";
import OfflineExhibitorTable from "./OfflineExhibitorTable";
import CreateOfflineExhibitor from "./CreateOfflineExhibitor";
import UpdateOfflineExhibitor from "./UpdateOfflineExhibitor";
import DeleteOfflineExhibitor from "./DeleteOfflineExhibitor";
import ExportOfflineExhibitorsExcel from "./ExportOfflineExhibitorsExcel";

const NewExhibitorList: React.FC = () => {
	const navigate = useNavigate();
	const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
	const [editModalState, setEditModalState] = useState<{
		open: boolean;
		id: number | null;
	}>({
		open: false,
		id: null,
	});
	const [deleteModalState, setDeleteModalState] = useState<{
		open: boolean;
		id: number | null;
		name: string;
	}>({
		open: false,
		id: null,
		name: "",
	});

	const [pagination, setPagination] = useState({
		limit: 10,
		skip: 0,
	});

	const [searchQuery, setSearchQuery] = useState<string>("");

	const { data, isLoading } = useGetQueryWithTotal<IOfflineExhibitorListItem[]>({
		endPoint: "/api/v1/admin/offline-exhibitor",
		queryKeys: ["OfflineExhibitors"],
		params: {
			limit: pagination.limit,
			skip: pagination.skip,
			...(searchQuery.trim() ? { searchQuery: searchQuery.trim() } : {}),
		},
	});

	const handleView = (id: number) => {
		navigate(`/exhibitor/details/${id}`);
	};

	const handleEdit = (id: number) => {
		setEditModalState({ open: true, id });
	};

	const handleDelete = (id: number, name: string) => {
		setDeleteModalState({ open: true, id, name });
	};

	return (
		<>
			<CommCard
				showCardHeader
				showSearch
				showButton
				searchPlaceHolder="Search by exhibitor name..."
				btnText="Create Exhibitor"
				headerTitle={`Exhibitor List (${data?.total ?? 0})`}
				handleSearchFun={(query) => {
					setSearchQuery(query);
					setPagination((prev) => ({ ...prev, skip: 0 }));
				}}
				handleBtnForModalFun={() => setOpenCreateModal(true)}
				helmetTitle="BTTF - Offline Exhibitor List"
				componentChildren={
					<ExportOfflineExhibitorsExcel
						searchQuery={searchQuery}
						totalCount={data?.total}
						currentData={data?.data}
						isLoading={isLoading}
					/>
				}
			>
				{/* Exhibitors Table */}
				<OfflineExhibitorTable
					data={data?.data || []}
					isLoading={isLoading}
					paginationSkip={pagination.skip}
					onView={handleView}
					onEdit={handleEdit}
					onDelete={handleDelete}
				/>

				{/* Pagination */}
				<Box p={2}>
					<CommonPagination
						total={data?.total}
						paginationCalFun={(limit: number, skip: number) => {
							setPagination({ limit, skip });
						}}
					/>
				</Box>

				{/* Create Exhibitor Modal */}
				<CreateOfflineExhibitor
					open={openCreateModal}
					handleClose={() => setOpenCreateModal(false)}
				/>

				{/* Update Exhibitor Modal */}
				<UpdateOfflineExhibitor
					open={editModalState.open}
					handleClose={() =>
						setEditModalState((prev) => ({ ...prev, open: false }))
					}
					exhibitorId={editModalState.id}
				/>


				{/* Delete Confirmation Dialog */}
				<DeleteOfflineExhibitor
					open={deleteModalState.open}
					handleClose={() =>
						setDeleteModalState((prev) => ({ ...prev, open: false }))
					}
					exhibitorId={deleteModalState.id}
					exhibitorName={deleteModalState.name}
				/>
			</CommCard>
		</>
	);
};

export default NewExhibitorList;
